import React, { useCallback, useEffect, useState } from 'react';
import { GoogleMap, Marker, MarkerClusterer } from '@react-google-maps/api';
import * as styles from './map.module.scss';
import { Coords, Expert } from '../section/MapSection';
import { clusterStyles } from './clusterStyles';
import { useMapContext } from '../context/mapContext';
import { PanTo } from './PanMap';
import { usePageContext } from '../context/pageContext';
import useWindowSize from '../hooks/useWindowSize';

const center: google.maps.LatLngLiteral = {
  lat: 43.48,
  lng: -1.55,
};

interface Props {
  data: Expert[];
  boundsCoords: Coords[];
  isLoaded: boolean;
}

const icons = {
  Rehabilitation: 'orange',
  'Uro & Pelvic Floor': 'violet',
  Sport: 'red',
  'Physio Aesthetic': 'brown',
  Equine: 'green',
};

function debounce(cb, delay = 500) {
  let timeout;

  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      cb(...args);
    }, delay);
  };
}

export const Map = ({ data, boundsCoords, isLoaded }: Props) => {
  const { selected, setSelected, panCoords, setMapAreaCoords } = useMapContext();
  const [map, setMap] = useState<google.maps.Map | undefined>(undefined);
  const { width } = useWindowSize();

  const onLoad = useCallback(
    function callback(map: google.maps.Map) {
      const bounds = new window.google.maps.LatLngBounds(center);
      // const bounds = new window.google.maps.LatLngBounds(center);
      // boundsCoords &&
      //   boundsCoords.map((item: any) => {
      //     // locations.map((item: any) => {
      //     bounds.extend(item);
      //   });
      // map.fitBounds(bounds);
      var listener = map?.addListener('idle', () => {
        map.setZoom(2.5);
        google.maps.event.removeListener(listener as google.maps.MapsEventListener);
      });

      setMap(map);
    },
    [boundsCoords],
  );

  const getMapAreaCoords = (map) => {
    if (map?.getBounds()) {
      const keys = Object.keys(map?.getBounds() ?? {});
      if (keys.length) {
        const lat = map?.getBounds()?.[keys[0]];
        const lng = map?.getBounds()?.[keys[1]];
        setMapAreaCoords({
          lat: {
            low: parseFloat(lat.lo),
            high: parseFloat(lat.hi),
          },
          lng: {
            low: parseFloat(lng.lo),
            high: parseFloat(lng.hi),
          },
        });
      }
    }
  };

  useEffect(() => {
    debounce(() => getMapAreaCoords(map));
  }, [panCoords]);

  useEffect(() => {
    if (map) map.setZoom(2.5);
  }, []);

  return isLoaded ? (
    <GoogleMap
      onClick={() => selected && width < 1024 && setSelected(undefined)}
      mapContainerClassName={styles.wrapper}
      onBoundsChanged={debounce(() => getMapAreaCoords(map))}
      center={center}
      options={{
        panControl: true,
        streetViewControl: false,
        mapTypeControl: false,
        scrollwheel: true,
        zoomControl: true,
        fullscreenControl: false,
        zoomControlOptions: {
          position: google.maps.ControlPosition.RIGHT_CENTER,
        },
        minZoom: 2.5,
        maxZoom: 18,
      }}
      onLoad={onLoad}
    >
      <PanTo />
      <MarkerClusterer
        styles={clusterStyles}
        clusterClass={styles.customClustericon}
        maxZoom={width >= 1024 ? 13 : 12}
      >
        {(clusterer) => (
          <>
            {data?.map((expert, index) => {
              if (!expert) return null;

              return (
                <Marker
                  key={`marker-${expert.id}`}
                  position={{
                    lng: parseFloat(expert.longitude.replace(',', '.')),
                    lat: parseFloat(expert.latitude.replace(',', '.')),
                  }}
                  clusterer={clusterer}
                  onClick={() => {
                    setSelected(expert);
                    if (typeof window !== 'undefined')
                      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                  }}
                  icon={
                    selected?.id === expert.id
                      ? `/svg/poi-black-${icons[expert.expertise[0]] ?? 'orange'}.svg`
                      : `/svg/poi-${icons[expert.expertise[0]] ?? 'orange'}.svg`
                  }
                />
              );
            })}
          </>
        )}
      </MarkerClusterer>
    </GoogleMap>
  ) : (
    <></>
  );
};
